import { useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceStatus } from '@features/invoices/enums/invoiceStatus'
import { useInvalidateInvoices } from '@features/invoices/hooks/useInvalidateInvoices'
import { mapBillyExternalInvoiceToInvoice } from '@features/invoices/utils/mapBillyExternalInvoiceToInvoice'
import { SubjectReconciliationSuggestionCard } from '@features/paymentReconciliationModal/elements/SubjectReconciliationSuggestionCard'
import {
  useDeleteSubjectReconciliationSuggestion,
  useSubjectReconciliationSuggestions,
} from '@features/paymentReconciliationModal/hooks'
import { reconciliationSuggestHasDifference } from '@features/paymentReconciliationModal/utils'
import { notifyPaymentRegistrationError } from '@features/paymentReconciliationModal/utils/notifyPaymentRegistrationError'
import {
  BankMatchRejectionReason,
  BankMatchRejectionReasonValue,
  BankMatchUiFlow,
  UiSource,
} from '@features/reconciliation/enums'
import { InvoiceType } from '@features/reconciliation/types/invoiceType'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useInvoice } from '@views/externalInvoices/routes/InvoiceView/contexts/invoiceContext'

import { queryClient } from '../../../../config/queryClient'
import { ModalId } from '../../../../enums/modalId'
import { QueryKeys } from '../../../../enums/queryKeys'
import { Scope } from '../../../../enums/scope'
import { useSegment } from '../../../../hooks'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { useReconcileInvoice } from '../../hooks/useReconcileInvoice'

export const ExternalInvoiceReconciliationSuggestion = (): ReactElement | null => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const { invoice, billyApiExternalInvoice, billyApiExternalInvoiceLoading } = useInvoice()
  const { organization } = useUserOrganization()
  const { open: openPaymentModal } = useModal(ModalId.InvoiceReconciliationModal)
  const { invalidateExternalInvoice, invalidateExternalInvoices } = useInvalidateInvoices()

  // Map the Billy external invoice to the Invoice shape so that useSubjectReconciliationSuggestions
  // correctly detects the subject type as InvoiceType.ExternalInvoice (via isExternalInvoice check
  // which relies on accountingBasis being present after the spread in mapBillyExternalInvoiceToInvoice).
  const mappedSubject = useMemo(
    () => (billyApiExternalInvoice ? mapBillyExternalInvoiceToInvoice(billyApiExternalInvoice) : undefined),
    [billyApiExternalInvoice],
  )

  const { suggestions, isLoading: isLoadingSuggestions } = useSubjectReconciliationSuggestions(mappedSubject)

  const { deleteSuggestion, isProcessing: isProcessingDelete } = useDeleteSubjectReconciliationSuggestion({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.InvoiceReconciliationMatches, billyApiExternalInvoice?.id])
    },
    onError: () => {
      notifyPaymentRegistrationError(t)
    },
  })

  const handleDeleteSuggestion = useCallback(
    (rejectionReason?: BankMatchRejectionReasonValue) => {
      if (!organization?.id || !suggestions?.length) {
        return
      }
      deleteSuggestion({
        organizationId: organization.id,
        suggestion: suggestions[0],
        uiSource: UiSource.InvoiceScreen,
        rejectionReason,
      })
    },
    [organization?.id, suggestions, deleteSuggestion],
  )

  const onSuccessReconciliation = useCallback(() => {
    /* eslint-disable @typescript-eslint/naming-convention */
    track('Invoice Payment Created (Client)', {
      invoice: billyApiExternalInvoice?.id,
      is_auto_matched: true,
      payment_mode: 'pay and reconcile',
    })
    track('XXX - Reconciliation - Reconciliation Suggestion Accepted (Client)')

    /** After a successful reconciliation, we remove the suggestion from the database.
     * This prevents the same suggestion from being shown again if the payment is voided in the future.
     */
    handleDeleteSuggestion(BankMatchRejectionReason.SuggestionAccepted)

    // Refresh external invoice queries so ExternalInvoicePaymentPane reflects the new payment.
    if (billyApiExternalInvoice?.id) {
      invalidateExternalInvoice(billyApiExternalInvoice.id)
    }
    invalidateExternalInvoices()
  }, [
    track,
    billyApiExternalInvoice?.id,
    handleDeleteSuggestion,
    invalidateExternalInvoice,
    invalidateExternalInvoices,
  ])

  const reconciliationTransaction = suggestions?.[0]?.transactions?.[0]
  const isCrossCurrency = reconciliationTransaction?.currency !== mappedSubject?.currencyId
  const crossCurrencyPaymentAmount = isCrossCurrency ? mappedSubject?.balance : undefined

  const { reconcileSubject: reconcileInvoice, isProcessing: isProcessingReconcile } = useReconcileInvoice({
    invoiceId: billyApiExternalInvoice?.id,
    invoiceType: InvoiceType.ExternalInvoice,
    organizationId: organization?.id,
    bankLineId: reconciliationTransaction?.id,
    crossCurrencyPaymentAmount,
    isAutoMatch: true,
    onSuccess: onSuccessReconciliation,
    uiSource: UiSource.InvoiceScreen,
    uiFlow: BankMatchUiFlow.Matched,
  })

  const hasDifference = reconciliationSuggestHasDifference(reconciliationTransaction, mappedSubject)
  const isLoading = isLoadingSuggestions || billyApiExternalInvoiceLoading
  const isProcessing = isProcessingDelete || isProcessingReconcile

  const handleReject = useCallback(() => {
    if (!organization?.id || !suggestions?.length) {
      return
    }

    track('XXX - Reconciliation - Reconciliation Suggestion Rejected (Client)')
    handleDeleteSuggestion()
  }, [suggestions, organization?.id, handleDeleteSuggestion, track])

  const handleAccept = useCallback(() => {
    if (hasDifference) {
      openPaymentModal()
      return
    }

    reconcileInvoice()
  }, [hasDifference, openPaymentModal, reconcileInvoice])

  if (
    !isAuthorized(Scope.BankReconciliationAutoMatch) ||
    invoice?.status?.status === InvoiceStatus.Paid ||
    billyApiExternalInvoice?.balance === 0 ||
    isLoading ||
    !suggestions?.length
  ) {
    return null
  }

  return (
    <SubjectReconciliationSuggestionCard
      handleAcceptSuggestion={handleAccept}
      handleRejectSuggestion={handleReject}
      isProcessingSuggestion={isProcessing}
      isProcessingDelete={isProcessingDelete}
      isProcessingReconcile={isProcessingReconcile}
      subjectType={InvoiceType.ExternalInvoice}
      suggestions={suggestions}
    />
  )
}
